import { reverse } from 'lodash'

/**
 * Options for currency formatting
 */
export interface CurrencyOptions {
    /** The number to format */
    number: number
    /** Number of decimal places (false = no decimals) */
    decimal?: number | false
    /** Locale for formatting (e.g., 'en-US', 'th-TH') */
    locale?: string
    /** ISO 4217 currency code (e.g., 'USD', 'THB') */
    currency: string
    /** Currency symbol to use (null = use code) */
    symbol?: string | null
    /** Whether to swap currency position (e.g., "100 USD" vs "USD 100") */
    swapPosition?: boolean
}

/**
 * Formats currency position (swaps if needed)
 * @internal
 */
function formatCurrencyPosition(format: string, number: number, swapPosition: boolean): string {
    if (!swapPosition) {
        return format
    }

    let swapParts = format.split(/\s/)
    swapParts = reverse(swapParts)

    if (number < 0) {
        return `-${swapParts[0]?.replace('-', '') || ''} ${swapParts[1]?.replace('-', '') || ''}`.trim()
    }

    return `${swapParts[0] || ''} ${swapParts[1] || ''}`.trim()
}

/**
 * Formats a number as currency
 *
 * @example
 * ```ts
 * currency({ number: 1500, currency: 'THB', locale: 'th-TH' })
 * // Returns: "1,500 THB"
 *
 * currency({ number: 1500, currency: 'USD', locale: 'en-US', swapPosition: false })
 * // Returns: "USD 1,500"
 * ```
 */
function currency({ number, decimal = false, locale = 'en-US', currency, symbol, swapPosition = true }: CurrencyOptions): string {
    const formatOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        currencyDisplay: symbol ? 'narrowSymbol' : 'code',
        minimumFractionDigits: decimal ? decimal : 0
    }

    try {
        const format = new Intl.NumberFormat(locale, formatOptions).format(number)
        return formatCurrencyPosition(format, number, swapPosition)
    } catch {
        // Fallback for unsupported narrowSymbol
        const fallbackOptions: Intl.NumberFormatOptions = {
            ...formatOptions,
            currencyDisplay: symbol ? 'symbol' : 'code'
        }
        const format = new Intl.NumberFormat(locale, fallbackOptions).format(number)
        return formatCurrencyPosition(format, number, swapPosition)
    }
}

/**
 * Options for number formatting
 */
export interface FormatNumberOptions {
    /** Locale for formatting (default: 'en-US') */
    locale?: string
    /** Maximum fraction digits (default: 0) */
    maximumFractionDigits?: number
    /** Use grouping separators (default: true) */
    useGrouping?: boolean
}

/**
 * Formats a number with locale-specific separators
 *
 * @example
 * ```ts
 * formatNumber(1500000)
 * // Returns: "1,500,000"
 *
 * formatNumber(1500.567, { locale: 'de-DE', maximumFractionDigits: 2 })
 * // Returns: "1.500,57"
 * ```
 */
function formatNumber(number: number, options: FormatNumberOptions = {}): string {
    const { locale = 'en-US', maximumFractionDigits = 0, useGrouping = true } = options

    return number.toLocaleString(locale, {
        maximumFractionDigits,
        useGrouping
    })
}

/**
 * Formats a number as percentage
 *
 * @example
 * ```ts
 * formatPercent(0.1567)
 * // Returns: "15.67%"
 * ```
 */
function formatPercent(number: number, options: { locale?: string; decimals?: number } = {}): string {
    const { locale = 'en-US', decimals = 2 } = options

    return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(number)
}

export const NumberHelpers = {
    currency,
    formatNumber,
    formatPercent
}
